import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, RefreshCw, Loader2, X, ShieldCheck, ShieldX, Filter, BadgeCheck } from "lucide-react";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const FILTERS = [
  ["all", "All"], ["under_scrutiny", "Under scrutiny"],
  ["inducted", "Inducted"], ["rejected", "Rejected"],
];

const STATUS_TONE = {
  under_scrutiny: "text-[#B87333] border-[#B87333]/40",
  inducted:       "text-[#D8B23E] border-[#D8B23E]/60",
  rejected:       "text-[#E5484D] border-[#E5484D]/40",
};

export function AdminBigCapLeague({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/big-cap/applications");
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const arr = data?.applications || [];
    if (filter === "all") return arr;
    return arr.filter(r => r.status === filter);
  }, [data, filter]);

  return (
    <div data-testid="admin-big-cap" className="space-y-6">
      <div className="flex items-start gap-4 border-b border-white/10 pb-6">
        <div className="h-12 w-12 rounded-sm bg-gradient-to-br from-[#D8B23E] to-[#B87333] text-black flex items-center justify-center shrink-0">
          <Crown size={20}/>
        </div>
        <div className="flex-1">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Big-Cap League · Admin</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Executive scrutiny queue.</h2>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            Only you can click <em>Induct into League</em>. Approved members get a branded certificate and Elite Gold theme.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="text-[#8A8A82] hover:text-[#F5F5F0] p-1" data-testid="bigcap-refresh">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] inline-flex items-center gap-1 pr-2">
          <Filter size={11}/> Filter
        </span>
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`bigcap-filter-${k}`}
                  className={`font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm border ${
                    filter === k ? "bg-[#D8B23E] text-black border-[#D8B23E]" : "border-white/15 text-[#8A8A82] hover:text-[#F5F5F0]"
                  }`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-10 text-center font-body text-sm text-[#8A8A82]">
          <Loader2 size={16} className="inline animate-spin mr-2"/> Loading applications…
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-10 text-center font-body text-sm text-[#8A8A82]">
          No applications in this state.
        </div>
      ) : (
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40">
              <tr>
                {["Applicant", "Role", "State", "Turnover", "Storage", "Trades", "Deposit", "Status"].map(h => (
                  <th key={h} className="text-left font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] px-3 py-2 border-b border-white/10">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} data-testid={`bigcap-row-${r.user_id}`}
                    onClick={() => setActive(r)}
                    className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer">
                  <td className="px-3 py-3">
                    <div className="font-body text-[13px] text-[#F5F5F0]">{r.business_name || r.email}</div>
                    <div className="font-body text-[10px] text-[#8A8A82]">{r.email}</div>
                  </td>
                  <td className="px-3 py-3 font-body text-[12px] text-[#C9C9C2] capitalize">{r.role}</td>
                  <td className="px-3 py-3 font-body text-[12px] text-[#C9C9C2]">{r.state || "—"}</td>
                  <td className="px-3 py-3 font-mono text-[12px] text-[#D8B23E]">{inr(r.scrutiny?.audited_turnover_inr || 0)}</td>
                  <td className="px-3 py-3 font-mono text-[12px] text-[#F5F5F0]">{Number(r.scrutiny?.storage_capacity_mt || 0).toLocaleString("en-IN")} MT</td>
                  <td className="px-3 py-3 font-mono text-[12px] text-[#F5F5F0]">{r.scrutiny?.trades_completed || 0}</td>
                  <td className="px-3 py-3">
                    {r.deposit_paid ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#3DBB84]/40 text-[#3DBB84] px-2 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em]">
                        <BadgeCheck size={9}/> Locked
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-white/15 text-[#8A8A82] px-2 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em]">Not paid</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em] ${STATUS_TONE[r.status] || "text-[#8A8A82] border-white/20"}`}>
                      {r.status.replace(/_/g, " ")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {active && <InductDrawer api={api} row={active}
                                  onClose={() => setActive(null)}
                                  onUpdated={() => { load(); setActive(null); }}/>}
      </AnimatePresence>
    </div>
  );
}

function InductDrawer({ api, row, onClose, onUpdated }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (decision) => {
    if (decision === "rejected" && !reason.trim()) { toast.error("Reason required for rejection"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/big-cap/${row.user_id}/induct`,
                     { decision, reason: reason.trim() });
      toast.success(decision === "induct" ? "Inducted · certificate issued" : "Rejected");
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const canDecide = row.status === "under_scrutiny";
  const s = row.scrutiny || {};

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-stretch justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
        className="bg-[#050506] w-full max-w-2xl border-l border-[#D8B23E]/30 h-full overflow-y-auto"
        data-testid="bigcap-decision-drawer">
        <div className="sticky top-0 z-10 bg-[#050506]/95 backdrop-blur flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Executive Scrutiny</div>
            <div className="font-serif text-xl text-[#F5F5F0]">{row.business_name || row.email}</div>
            <div className="font-body text-[11px] text-[#8A8A82]">{row.email} · {row.role} · {row.state}</div>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={20}/></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Legal entity", s.legal_entity_name],
              ["Incorporation year", s.incorporation_year],
              ["GSTIN", s.gstin],
              ["PAN", s.pan_number],
              ["CIN", s.cin_number],
              ["UBO", s.ubo_name],
              ["HQ address", s.hq_address],
              ["Directors", s.directors],
              ["Turnover source", s.audited_turnover_source],
              ["Audited turnover · FY-25", inr(s.audited_turnover_inr || 0)],
              ["Turnover · FY-24", s.turnover_prev1_inr != null ? inr(s.turnover_prev1_inr) : "—"],
              ["Turnover · FY-23", s.turnover_prev2_inr != null ? inr(s.turnover_prev2_inr) : "—"],
              ["EBITDA", s.ebitda_inr != null ? inr(s.ebitda_inr) : "—"],
              ["Net worth", s.networth_inr != null ? inr(s.networth_inr) : "—"],
              ["Working capital", s.working_capital_inr != null ? inr(s.working_capital_inr) : "—"],
              ["Primary banker", `${s.banker_name || "—"} · ${s.banker_branch || ""}`],
              ["RM", `${s.banker_rm_name || "—"} · ${s.banker_rm_contact || ""}`],
              ["Storage capacity", `${Number(s.storage_capacity_mt || 0).toLocaleString("en-IN")} MT`],
              ["Warehouses", s.warehouse_locations],
              ["Fleet size", `${s.fleet_size} (owned ${s.fleet_owned ?? "—"} · leased ${s.fleet_leased ?? "—"})`],
              ["In-house labour", s.inhouse_labour_count ?? "—"],
              ["3PL partners", s.third_party_logistics || "—"],
              ["Trades on Vinci", s.trades_completed],
              ["ISO certification", s.iso_cert_no || "—"],
              ["BIS certification", s.bis_cert_no || "—"],
              ["MSME / Udyam", s.msme_cert_no || "—"],
              ["Safety audit", s.safety_audit_date || "—"],
              ["LTIFR", s.safety_kpi_ltifr ?? "—"],
              ["Signatory", `${s.signatory_name || "—"} · ${s.signatory_designation || "—"}`],
              ["Signatory email", s.signatory_email || "—"],
              ["Signatory DIN", s.signatory_din || "—"],
              ["Sanctions declaration", s.sanction_self_declaration ? "Signed" : "—"],
              ["Code of conduct", s.accepts_code_of_conduct ? "Accepted" : "—"],
            ].map(([k, v]) => (
              <div key={k} className="border border-white/10 rounded-sm bg-[#0a0a0a] p-3">
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{k}</div>
                <div className="font-body text-sm text-[#F5F5F0] mt-1 break-words">{v ?? "—"}</div>
              </div>
            ))}
          </div>

          {/* Marquee projects */}
          {(s.marquee_project_1 || s.marquee_project_2 || s.marquee_project_3 || s.marquee_project_4 || s.marquee_project_5) && (
            <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Marquee projects · last 5 years</div>
              <ol className="list-decimal list-inside space-y-1 font-body text-sm text-[#F5F5F0]">
                {[1,2,3,4,5].map(n => s[`marquee_project_${n}`] && <li key={n}>{s[`marquee_project_${n}`]}</li>)}
              </ol>
            </div>
          )}

          {(s.open_litigations || s.tax_standing) && (
            <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Compliance disclosures</div>
              {s.open_litigations && <><div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] mt-2">Open litigations</div><div className="font-body text-sm text-[#F5F5F0] whitespace-pre-wrap">{s.open_litigations}</div></>}
              {s.tax_standing && <><div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] mt-2">Tax standing</div><div className="font-body text-sm text-[#F5F5F0] whitespace-pre-wrap">{s.tax_standing}</div></>}
            </div>
          )}

          {s.key_projects_delivered && (
            <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Delivered projects</div>
              <div className="font-body text-sm text-[#F5F5F0] whitespace-pre-wrap">{s.key_projects_delivered}</div>
            </div>
          )}
          {s.balance_sheet_summary && (
            <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Balance sheet summary</div>
              <div className="font-body text-sm text-[#F5F5F0] whitespace-pre-wrap">{s.balance_sheet_summary}</div>
            </div>
          )}

          {canDecide ? (
            <div className="border border-[#D8B23E]/40 bg-[#D8B23E]/[0.05] rounded-sm p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Executive decision</div>
              <textarea data-testid="bigcap-decide-reason" value={reason} onChange={e => setReason(e.target.value)}
                        rows={3} placeholder="Reason (required only for rejection)"
                        className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
              <div className="flex gap-3 mt-3">
                <button onClick={() => decide("induct")} disabled={busy} data-testid="bigcap-induct"
                        className="inline-flex items-center gap-2 bg-[#D8B23E] hover:bg-[#c5a233] disabled:opacity-40 text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm">
                  {busy ? <Loader2 size={12} className="animate-spin"/> : <Crown size={12}/>} Induct into League
                </button>
                <button onClick={() => decide("rejected")} disabled={busy} data-testid="bigcap-reject"
                        className="inline-flex items-center gap-2 border border-[#E5484D]/50 text-[#E5484D] hover:bg-[#E5484D]/10 disabled:opacity-40 font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm">
                  <ShieldX size={12}/> Reject
                </button>
              </div>
            </div>
          ) : row.membership_no ? (
            <div className="border border-[#D8B23E]/40 bg-[#D8B23E]/[0.06] rounded-sm p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] inline-flex items-center gap-1">
                <ShieldCheck size={12}/> Inducted
              </div>
              <div className="font-body text-sm text-[#F5F5F0] mt-1 font-mono">{row.membership_no}</div>
            </div>
          ) : (
            <div className="font-body text-[11px] text-[#8A8A82]">Status: {row.status.replace(/_/g," ")}</div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
